import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  get_active_class_terms,
  get_class_courses,
  get_classes_by_course_term,
} from "../../../api/Class";
import { get_course_all_year_data } from "../../../api/common";
import {
  get_course_term_data,
  add_course_term_data,
} from "../../../api/branches/Branch";
import { student_enroll_view } from "../../../api/Student_Enroll";

const StudentEnrollment = () => {
  const navigate = useNavigate();
  const { student_id } = useParams();
  const branchid = localStorage.getItem("b_id");
  const userData = JSON.parse(localStorage.getItem("userData"));

  // Student info
  const [studentInfo, setStudentInfo] = useState(null);

  // Dropdown data
  const [yearData, setYearData] = useState([]);
  const [termData, setTermData] = useState([]);
  const [courseData, setCourseData] = useState([]);
  const [classList, setClassList] = useState([]);

  // Selected values
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedClass, setSelectedClass] = useState("");

  // Enrollment data (term/subject/unit checkboxes)
  const [termEnrollData, setTermEnrollData] = useState({});
  const [termSelectedUnitsByCourse, setTermSelectedUnitsByCourse] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load student info
  useEffect(() => {
    async function loadStudent() {
      const result = await student_enroll_view({ id: student_id });
      if (result?.data) {
        setStudentInfo(result.data);
      }
    }
    loadStudent();
  }, [student_id]);

  // Load years (auto-select current year)
  useEffect(() => {
    async function loadYears() {
      const result = await get_course_all_year_data();
      const years = result?.data || [];
      setYearData(years);
      const currentYear = new Date().getFullYear().toString();
      if (years.some((y) => String(y.current_year) === currentYear)) {
        setSelectedYear(currentYear);
      }
    }
    loadYears();
  }, []);

  // Load terms when year changes (auto-select first/active term)
  useEffect(() => {
    async function loadTerms() {
      if (!selectedYear) {
        setTermData([]);
        return;
      }
      const result = await get_active_class_terms(selectedYear, userData?.data?.branchstate);
      const terms = result?.data || [];
      setTermData(terms);
      if (terms.length === 1) {
        setSelectedTerm(String(terms[0].id));
      } else if (terms.length > 0) {
        const activeTerm = terms.find((t) => t.is_active === 1 || t.is_active === true);
        if (activeTerm) {
          setSelectedTerm(String(activeTerm.id));
        }
      }
    }
    setSelectedTerm("");
    setSelectedCourse("");
    setSelectedClass("");
    setCourseData([]);
    setClassList([]);
    setTermEnrollData({});
    loadTerms();
  }, [selectedYear]);

  // Load courses when term changes
  useEffect(() => {
    async function loadCourses() {
      if (!selectedYear || !selectedTerm || !studentInfo?.grade_id) {
        setCourseData([]);
        return;
      }
      const result = await get_class_courses(studentInfo.grade_id, selectedTerm, selectedYear);
      setCourseData(result?.data || []);
    }
    setSelectedCourse("");
    setSelectedClass("");
    setClassList([]);
    setTermEnrollData({});
    loadCourses();
  }, [selectedTerm, studentInfo?.grade_id]);

  // Load classes when course changes
  useEffect(() => {
    async function loadClasses() {
      if (!selectedCourse || !selectedTerm) {
        setClassList([]);
        return;
      }
      const result = await get_classes_by_course_term(branchid, selectedCourse, selectedTerm);
      setClassList(result?.data || []);
    }
    setSelectedClass("");
    setTermEnrollData({});
    loadClasses();
  }, [selectedCourse]);

  // Load enrollment data (term/subject/unit) when course is selected
  useEffect(() => {
    async function loadEnrollData() {
      if (!selectedCourse) {
        setTermEnrollData({});
        return;
      }
      const result = await get_course_term_data(selectedCourse);
      const course_selected_data = result?.data || [];
      const termsByCourseId = {};
      course_selected_data.forEach((course) => {
        const courseId = course.course_id;
        if (!termsByCourseId[courseId]) {
          termsByCourseId[courseId] = {
            courseName: course.course_name,
            courseTypeName: course.course_type_name,
            course_type_id: course.course_type_id,
            terms: [],
          };
        }
        course.terms.forEach((term) => {
          termsByCourseId[courseId].terms.push({
            termName: term.term_name,
            term_id: term.term_id,
            subjects: term.subjects,
          });
        });
      });
      setTermEnrollData(termsByCourseId);
      setTermSelectedUnitsByCourse({});
    }
    loadEnrollData();
  }, [selectedCourse]);

  // Unit checkbox handler
  const handleLectureInputChange = (e, courseId, termIndex, subjectIndex, unitId) => {
    setTermSelectedUnitsByCourse((prev) => {
      const updated = { ...prev };
      if (!updated[courseId]) updated[courseId] = {};
      if (!updated[courseId][termIndex]) updated[courseId][termIndex] = {};
      if (!updated[courseId][termIndex][subjectIndex]) {
        updated[courseId][termIndex][subjectIndex] = [];
      }
      const isSelected = updated[courseId][termIndex][subjectIndex].includes(unitId);
      if (isSelected) {
        updated[courseId][termIndex][subjectIndex] = updated[courseId][termIndex][subjectIndex].filter((id) => id !== unitId);
      } else {
        updated[courseId][termIndex][subjectIndex].push(unitId);
      }
      if (updated[courseId][termIndex][subjectIndex].length === 0) delete updated[courseId][termIndex][subjectIndex];
      if (Object.keys(updated[courseId][termIndex] || {}).length === 0) delete updated[courseId][termIndex];
      if (Object.keys(updated[courseId] || {}).length === 0) delete updated[courseId];
      return updated;
    });
  };

  // Term checkbox handler (select/deselect all units in a term)
  const handleTermCheckboxChange = (courseId, termIndex) => {
    setTermSelectedUnitsByCourse((prev) => {
      const updated = { ...prev };
      if (!updated[courseId]) updated[courseId] = {};
      const allSelected = termEnrollData[courseId]?.terms[termIndex]?.subjects.every(
        (subject, subjectIndex) =>
          subject.units.every((unit) =>
            updated[courseId]?.[termIndex]?.[subjectIndex]?.includes(unit.unit_id)
          )
      );
      if (allSelected) {
        delete updated[courseId][termIndex];
      } else {
        if (!updated[courseId][termIndex]) updated[courseId][termIndex] = {};
        termEnrollData[courseId].terms[termIndex].subjects.forEach((subject, subjectIndex) => {
          updated[courseId][termIndex][subjectIndex] = subject.units.map((unit) => unit.unit_id);
        });
      }
      if (Object.keys(updated[courseId]?.[termIndex] || {}).length === 0) delete updated[courseId]?.[termIndex];
      if (Object.keys(updated[courseId] || {}).length === 0) delete updated[courseId];
      return updated;
    });
  };

  // Submit enrollment
  const onSubmit = async () => {
    if (isSubmitting) return;
    if (!selectedClass) {
      alert("Please select a class.");
      return;
    }
    setIsSubmitting(true);

    const submissionData = [];
    Object.keys(termEnrollData).forEach((courseId) => {
      const course = termEnrollData[courseId];
      const courseTerms = course?.terms || [];
      courseTerms.forEach((term, termIndex) => {
        const termUnits = termSelectedUnitsByCourse[courseId]?.[termIndex] || {};
        term.subjects.forEach((subject, subjectIndex) => {
          const allUnits = subject.units || [];
          allUnits.forEach((unit) => {
            const isUnitSelected = termUnits[subjectIndex]?.includes(unit.unit_id) || false;
            submissionData.push({
              course_id: courseId,
              term_name: term.termName,
              term_id: term.term_id,
              subject_id: subject.subject_id,
              unit_id: unit.unit_id,
              course_type_name: course.courseTypeName,
              course_type_id: course.course_type_id,
              course_name: course.courseName,
              status: isUnitSelected ? 1 : 0,
            });
          });
        });
      });
    });

    const payload = {
      student_id: [student_id],
      courses: submissionData,
    };

    const result = await add_course_term_data(payload, selectedClass, branchid);
    if (result?.status === true) {
      navigate("/branch/class-management/all");
    }
    setIsSubmitting(false);
  };

  const styles = {
    page: { padding: "20px 24px" },
    header: {
      display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20,
    },
    title: { fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" },
    backBtn: {
      width: 32, height: 32, borderRadius: "50%", border: "1px solid #ddd",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#555", textDecoration: "none", fontSize: 13,
      background: "#fff", transition: "all 0.2s",
    },
    studentBar: {
      display: "flex", gap: 24, padding: "10px 16px",
      background: "#f0f7ff", borderRadius: 8, marginBottom: 20,
      fontSize: 13, color: "#333", flexWrap: "wrap",
    },
    studentLabel: { color: "#666", marginRight: 4 },
    studentValue: { fontWeight: 600 },
    dropdownRow: {
      display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20,
    },
    fieldLabel: {
      fontSize: 11, fontWeight: 600, textTransform: "uppercase",
      color: "#888", marginBottom: 4, letterSpacing: "0.5px",
    },
    select: {
      width: "100%", padding: "8px 10px", border: "1px solid #ddd",
      borderRadius: 6, fontSize: 13, color: "#333",
      background: "#fff", outline: "none", cursor: "pointer",
    },
    selectDisabled: {
      width: "100%", padding: "8px 10px", border: "1px solid #eee",
      borderRadius: 6, fontSize: 13, color: "#aaa",
      background: "#fafafa", outline: "none", cursor: "not-allowed",
    },
    noClass: { fontSize: 11, color: "#e74c3c", marginTop: 4 },
    divider: { border: "none", borderTop: "1px solid #eee", margin: "0 0 16px 0" },
    termCard: {
      border: "1px solid #e8e8e8", borderRadius: 8,
      marginBottom: 12, overflow: "hidden",
    },
    termHeader: {
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", background: "#f8f9fa",
      borderBottom: "1px solid #e8e8e8", cursor: "pointer", userSelect: "none",
    },
    termCheckbox: { width: 16, height: 16, cursor: "pointer", accentColor: "#3b82f6", flexShrink: 0 },
    termName: { fontSize: 14, fontWeight: 600, color: "#333", flex: 1 },
    termToggle: { fontSize: 11, color: "#999", marginLeft: "auto" },
    termBody: { padding: "8px 14px 12px" },
    subjectRow: {
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "8px 0", borderBottom: "1px solid #f0f0f0",
    },
    subjectRowLast: {
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "8px 0",
    },
    subjectName: {
      width: 180, flexShrink: 0, fontSize: 13, fontWeight: 500,
      color: "#444", paddingTop: 4,
    },
    unitsWrap: {
      display: "flex", flexWrap: "wrap", gap: 6, flex: 1,
    },
    unitChip: (checked) => ({
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "4px 10px", borderRadius: 16, fontSize: 12,
      cursor: "pointer", userSelect: "none", transition: "all 0.15s",
      border: checked ? "1px solid #3b82f6" : "1px solid #ddd",
      background: checked ? "#eff6ff" : "#fff",
      color: checked ? "#1d4ed8" : "#666",
      fontWeight: checked ? 500 : 400,
    }),
    unitCheckbox: { width: 13, height: 13, cursor: "pointer", accentColor: "#3b82f6" },
    saveBar: {
      display: "flex", justifyContent: "flex-end",
      padding: "16px 0", marginTop: 4,
    },
    saveBtn: {
      padding: "10px 32px", background: "#3b82f6", color: "#fff",
      border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500,
      cursor: "pointer", transition: "all 0.2s",
    },
    saveBtnDisabled: {
      padding: "10px 32px", background: "#94a3b8", color: "#fff",
      border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500,
      cursor: "not-allowed",
    },
  };

  const [expandedTerms, setExpandedTerms] = useState({});
  const toggleTerm = (termIndex) => {
    setExpandedTerms((prev) => ({ ...prev, [termIndex]: !prev[termIndex] }));
  };

  // Reset expanded state when enroll data loads (all collapsed by default)
  useEffect(() => {
    setExpandedTerms({});
  }, [termEnrollData]);

  return (
    <div className="right-nav" id="dash-event" style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h4 style={styles.title}>Enroll Student</h4>
        <Link to="/branch/enroll/student_enroll-list" style={styles.backBtn}>
          <i className="fa fa-arrow-left"></i>
        </Link>
      </div>

      {/* Student Info */}
      {studentInfo && (
        <div style={styles.studentBar}>
          <span><span style={styles.studentLabel}>Student</span> <span style={styles.studentValue}>{studentInfo.first_name} {studentInfo.last_name}</span></span>
          <span><span style={styles.studentLabel}>Grade</span> <span style={styles.studentValue}>Year {studentInfo.grade}</span></span>
          {studentInfo.branch_name && (
            <span><span style={styles.studentLabel}>Branch</span> <span style={styles.studentValue}>{studentInfo.branch_name}</span></span>
          )}
        </div>
      )}

      {/* Cascade Dropdowns */}
      <div style={styles.dropdownRow}>
        <div>
          <div style={styles.fieldLabel}>Year</div>
          <select style={styles.select} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="">Select</option>
            {yearData?.map((elem) => (
              <option key={elem.current_year} value={elem.current_year}>{elem.current_year}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={styles.fieldLabel}>Term</div>
          <select style={selectedYear ? styles.select : styles.selectDisabled} value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)} disabled={!selectedYear}>
            <option value="">Select</option>
            {termData?.map((elem) => (
              <option key={elem.id} value={elem.id}>{elem.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={styles.fieldLabel}>Course</div>
          <select style={selectedTerm ? styles.select : styles.selectDisabled} value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)} disabled={!selectedTerm}>
            <option value="">Select</option>
            {courseData?.map((elem) => (
              <option key={elem.id} value={elem.id}>{elem.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={styles.fieldLabel}>Class</div>
          <select style={selectedCourse ? styles.select : styles.selectDisabled} value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)} disabled={!selectedCourse}>
            <option value="">Select</option>
            {classList?.map((elem) => (
              <option key={elem.id} value={elem.id}>{elem.class_name} ({elem.student_count})</option>
            ))}
          </select>
          {selectedCourse && classList?.length === 0 && (
            <div style={styles.noClass}>
              No classes found. <Link to="/branch/class-management/add" style={{ color: "#e74c3c", textDecoration: "underline" }}>Create one</Link>
            </div>
          )}
        </div>
      </div>

      {/* Term / Subject / Unit selection */}
      {selectedCourse && Object.keys(termEnrollData).length > 0 && (
        <>
          <hr style={styles.divider} />
          {Object.keys(termEnrollData).map((courseId) =>
            termEnrollData[courseId].terms
              .map((term, origIndex) => ({ ...term, origIndex }))
              .filter((term) => String(term.term_id) === String(selectedTerm))
              .map((term) => {
              const termIndex = term.origIndex;
              const isTermSelected = term.subjects.every(
                (subject, subjectIndex) =>
                  subject.units.every((unit) =>
                    termSelectedUnitsByCourse[courseId]?.[termIndex]?.[subjectIndex]?.includes(unit.unit_id)
                  )
              );
              const isExpanded = expandedTerms[termIndex] === true;
              return (
                <div key={termIndex} style={styles.termCard}>
                  <div style={styles.termHeader} onClick={() => toggleTerm(termIndex)}>
                    <input
                      type="checkbox" style={styles.termCheckbox}
                      checked={isTermSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleTermCheckboxChange(courseId, termIndex)}
                    />
                    <span style={styles.termName}>Term {term.termName}</span>
                    <span style={styles.termToggle}>
                      <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                    </span>
                  </div>
                  {isExpanded && (
                    <div style={styles.termBody}>
                      {term.subjects.map((subjectObj, subjectIndex) => (
                        <div key={subjectIndex}
                          style={subjectIndex < term.subjects.length - 1 ? styles.subjectRow : styles.subjectRowLast}>
                          <div style={styles.subjectName}>{subjectObj.subject_name}</div>
                          <div style={styles.unitsWrap}>
                            {subjectObj.units.map((unit, unitIndex) => {
                              const checked = termSelectedUnitsByCourse[courseId]?.[termIndex]?.[subjectIndex]?.includes(unit.unit_id) || false;
                              return (
                                <label key={unitIndex} style={styles.unitChip(checked)}>
                                  <input
                                    type="checkbox" style={styles.unitCheckbox}
                                    checked={checked}
                                    onChange={(e) => handleLectureInputChange(e, courseId, termIndex, subjectIndex, unit.unit_id)}
                                  />
                                  {unit.unit_name}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Save */}
          {selectedClass && (
            <div style={styles.saveBar}>
              <button
                style={isSubmitting ? styles.saveBtnDisabled : styles.saveBtn}
                disabled={isSubmitting} onClick={onSubmit}
              >
                {isSubmitting ? "Saving..." : "Save Enrollment"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentEnrollment;
